import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AssetCondition,
  AssetOwnership,
  AssetStatus,
  ClearanceStatus,
  EmployeeStatus,
  RepairStatus,
} from '@cfl/assets-shared';

import { api } from '@/lib/api';
import { assetRequestKeys } from '@/lib/asset-requests-api';
import { assetKeys } from '@/lib/assets-api';
import { employeeKeys } from '@/lib/employees-api';

/**
 * The lifecycle operations: assign, return, acknowledge, repair, exit.
 *
 * Grouped in one module rather than split per entity because they share one property
 * that shapes every hook here - each of them changes BOTH an asset and a person, so
 * each invalidates both namespaces. Splitting them per resource would put that
 * coupling in five files and invite one of them to forget half of it, which shows up
 * as a dashboard that keeps insisting a returned laptop is still out.
 */

function useLifecycleMutation<TInput, TResult>(
  mutationFn: (input: TInput) => Promise<TResult>,
  extraKeys: readonly (readonly unknown[])[] = [],
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: assetKeys.all });
      void queryClient.invalidateQueries({ queryKey: employeeKeys.all });
      for (const key of extraKeys) void queryClient.invalidateQueries({ queryKey: key });
    },
  });
}

// ---------------------------------------------------------------------------
// Assign / return / acknowledge
// ---------------------------------------------------------------------------

export interface AssignInput {
  assetId: string;
  employeeId: string;
  assignedAt: string;
  condition: 'NEW' | 'GOOD' | 'FAIR' | 'POOR';
  remarks?: string | null;
  /** Small items handed over with it - charger, bag. Not assets in their own right. */
  accessories: string[];
  /**
   * The asset request this handover settles, if any.
   *
   * The ONLY way a request becomes FULFILLED. There is no "mark fulfilled" endpoint, so
   * that status cannot exist without a real handover behind it - see
   * packages/shared/src/asset-requests.ts.
   */
  fulfilsRequestId?: string | null;

  /**
   * The machine's condition as it goes out.
   *
   * Written to two places by the API: the asset's live record, and a frozen copy on
   * this handover. The frozen copy is what a repair reported months later gets judged
   * against - see packages/shared/src/faults.ts.
   */
  faults?: string[];
  conditionNotes?: string | null;
  /** "Somebody actually looked." Sent even with no faults, because that is the only
   *  way an empty list can mean "checked, nothing wrong". */
  conditionChecked?: boolean;
}

export function useAssignAsset() {
  return useLifecycleMutation(
    (input: AssignInput) =>
      api.post<{ id: string; assetCode: string; employeeName: string }>('/assignments', input),
    // The request queue too: a handover may just have closed one, and the nav badge and
    // the queue page both have to stop showing it.
    [assetRequestKeys.all],
  );
}

export interface ReturnInput {
  assetId: string;
  returnedAt: string;
  condition: AssetCondition;
  reason?: string | null;
  remarks?: string | null;
  /** What actually came back. Anything issued and not listed here is reported missing. */
  returnedAccessories: string[];

  /**
   * The login on the DEVICE, so IT can start the machine up and check it.
   *
   * Required by the API when the asset's category says so and the company owns it;
   * refused outright for an employee's own device. Never the corporate Google password.
   */
  deviceUsername?: string | null;
  devicePassword?: string | null;
}

export function useReturnAsset() {
  return useLifecycleMutation((input: ReturnInput) =>
    api.post<{
      /** The hold period just closed. Photos of the returned machine attach to it. */
      assignmentId: string;
      assetCode: string;
      status: AssetStatus;
      employeeName: string;
      missingAccessories: string[];
    }>('/assignments/return', input),
  );
}

/**
 * Show one stored device password.
 *
 * A mutation rather than a query, and not cached anywhere: every call writes an audit
 * row naming who looked, which is the only thing standing between an entitled
 * administrator and an unobserved look at somebody's machine. A cached query would
 * make one audited request serve any number of unaudited views of the result.
 */
export function useRevealDeviceCredential() {
  return useMutation({
    mutationFn: (assignmentId: string) =>
      api.post<{ assetCode: string; username: string; password: string }>(
        `/assignments/${assignmentId}/credential/reveal`,
        {},
      ),
  });
}

/** Forget one, once the machine has been wiped. The username and the fact it was
 *  cleared both survive - see the column comment on deviceSecretClearedAt. */
export function useClearDeviceCredential() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (assignmentId: string) =>
      api.delete<{ cleared: boolean; assetCode: string }>(
        `/assignments/${assignmentId}/credential`,
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: assetKeys.all }),
  });
}

export interface MyAssignment {
  id: string;
  asset: {
    id: string;
    code: string;
    name: string;
    brand: string | null;
    model: string | null;
    serial: string | null;
    status: AssetStatus;
  };
  assignedAt: string;
  condition: AssetCondition;
  remarks: string | null;
  accessories: string[];
  returnedAccessories: string[];
  /** Populated only once returned. */
  missingAccessories: string[];
  assignedByName: string | null;
  acknowledgedAt: string | null;
  acknowledgedByName: string | null;
  returnedAt: string | null;
  returnCondition: AssetCondition | null;
  isOpen: boolean;
}

export const myAssetsKey = ['assignments', 'mine'] as const;

export function useMyAssets() {
  return useQuery({
    queryKey: myAssetsKey,
    queryFn: () => api.get<MyAssignment[]>('/assignments/mine'),
  });
}

export function useEmployeeAssignments(employeeId: string | null) {
  return useQuery({
    queryKey: ['assignments', 'employee', employeeId] as const,
    queryFn: () => api.get<MyAssignment[]>(`/assignments?employeeId=${employeeId}`),
    enabled: employeeId !== null,
  });
}

export function useAcknowledge() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string | null }) =>
      api.post<{ acknowledgedAt: string; alreadyAcknowledged: boolean; onBehalf: boolean }>(
        `/assignments/${id}/acknowledge`,
        { note },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: myAssetsKey });
      void queryClient.invalidateQueries({ queryKey: assetKeys.all });
      // The nav badge counts unacknowledged assets, and it lives on the session.
      void queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });
}

/**
 * The bulk version, for confirmations an administrator collected off-screen.
 *
 * Takes asset ids because that is what the register's selection holds; the API resolves
 * each to its one open handover and skips anything already confirmed.
 */
export function useAcknowledgeOnBehalf() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ assetIds, note }: { assetIds: string[]; note: string }) =>
      api.post<{ confirmed: number; skipped: number; codes: string[] }>(
        '/assignments/acknowledge-on-behalf',
        { assetIds, note },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: myAssetsKey });
      void queryClient.invalidateQueries({ queryKey: assetKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Repairs
// ---------------------------------------------------------------------------

export interface Repair {
  id: string;
  asset: { id: string; code: string; name: string; status: AssetStatus };
  /** Who holds it now, from the one open assignment. Null for pool stock. */
  holder: { id: string; employeeCode: string; name: string } | null;
  startedAt: string;
  completedAt: string | null;
  status: RepairStatus;
  reason: string;
  problem: string | null;
  vendor: string | null;
  cost: string | null;
  parts: string | null;
  remarks: string | null;
  loggedByName: string | null;
}

export const repairKeys = {
  all: ['repairs'] as const,
  list: (status?: string) => ['repairs', 'list', status ?? 'all'] as const,
  summary: ['repairs', 'summary'] as const,
};

export function useRepairs(status?: RepairStatus | '') {
  return useQuery({
    queryKey: repairKeys.list(status || undefined),
    queryFn: () => api.get<Repair[]>(`/repairs${status ? `?status=${status}` : ''}`),
  });
}

/** `enabled` because the shell reads this for a nav badge, and a plain employee may
 *  not call it - firing it anyway would put a 403 in the console on every page load. */
export function useRepairSummary(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: repairKeys.summary,
    queryFn: () => api.get<{ open: number; total: number; totalCost: string }>('/repairs/summary'),
    enabled: options.enabled ?? true,
  });
}

export interface OpenRepairInput {
  assetId: string;
  startedAt: string;
  reason: string;
  problem?: string | null;
  vendor?: string | null;
  cost?: number | null;
  parts?: string | null;
  remarks?: string | null;
}

export function useOpenRepair() {
  return useLifecycleMutation(
    (input: OpenRepairInput) => api.post<{ id: string; assetCode: string }>('/repairs', input),
    [repairKeys.all],
  );
}

export interface CloseRepairInput {
  status: 'COMPLETED' | 'CANCELLED';
  completedAt: string;
  cost?: number | null;
  parts?: string | null;
  remarks?: string | null;
  /** Where the asset lands. Defaults to whatever it was before the repair. */
  resultingStatus?: 'AVAILABLE' | 'DAMAGED' | 'RETIRED' | 'DISPOSED';
}

export function useCloseRepair(id: string) {
  return useLifecycleMutation(
    (input: CloseRepairInput) =>
      api.post<{ assetCode: string; status: AssetStatus }>(`/repairs/${id}/close`, input),
    [repairKeys.all],
  );
}

// ---------------------------------------------------------------------------
// Exit & clearance
// ---------------------------------------------------------------------------

export interface ExitRecord {
  id: string;
  employee: {
    id: string;
    employeeCode: string;
    name: string;
    department: string | null;
    status: EmployeeStatus;
  };
  exitDate: string;
  lastWorkingDate: string | null;
  reason: string | null;
  clearanceStatus: ClearanceStatus;
  comments: string | null;
  /** What was outstanding the day clearance was recorded - a snapshot, kept as-is. */
  outstandingAtClearance: number;
  /** What they still hold today. The gap between the two is what was recovered after
   *  they left. */
  outstandingNow: number;
  recordedByName: string | null;
}

export interface PendingAsset {
  assignmentId: string;
  asset: {
    id: string;
    code: string;
    name: string;
    brand: string | null;
    model: string | null;
    serial: string | null;
    /** Everything they hold is listed; only company property counts as outstanding.
     *  The exit dialog reads this with isCompanyProperty, which is the same rule the
     *  API applies before it will write CLEARED. */
    ownership: AssetOwnership;
  };
  assignedAt: string;
  acknowledged: boolean;
}

export const exitKeys = {
  all: ['exits'] as const,
  list: ['exits', 'list'] as const,
  summary: ['exits', 'summary'] as const,
  pending: (employeeId: string) => ['exits', 'pending', employeeId] as const,
};

/** `enabled` for the same reason the summaries take it: the Archive page reads this
 *  for clearance labels, and a viewer without exit rights must not fire a 403. */
export function useExits(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: exitKeys.list,
    queryFn: () => api.get<ExitRecord[]>('/exits'),
    enabled: options.enabled ?? true,
  });
}

export function useExitSummary(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: exitKeys.summary,
    queryFn: () =>
      api.get<{ pendingClearance: number; exitedHoldingAssets: number }>('/exits/summary'),
    enabled: options.enabled ?? true,
  });
}

/** Read again inside the exit dialog: between opening the form and submitting it, IT
 *  may have taken the laptop back - or issued another one. */
export function usePendingAssets(employeeId: string | null) {
  return useQuery({
    queryKey: exitKeys.pending(employeeId ?? ''),
    queryFn: () => api.get<PendingAsset[]>(`/exits/pending/${employeeId}`),
    enabled: employeeId !== null,
    // Never served from cache: this is the number the clearance rule is checked
    // against, and a stale one would show a Cleared button that the API then refuses.
    staleTime: 0,
  });
}

export interface RecordExitInput {
  employeeId: string;
  exitDate: string;
  lastWorkingDate?: string | null;
  reason?: string | null;
  clearanceStatus: ClearanceStatus;
  comments?: string | null;
}

export function useRecordExit() {
  return useLifecycleMutation(
    (input: RecordExitInput) => api.post<{ id: string; outstanding: number }>('/exits', input),
    [exitKeys.all],
  );
}

export function useUpdateClearance(exitId: string) {
  return useLifecycleMutation(
    (input: { clearanceStatus: ClearanceStatus; comments?: string | null }) =>
      api.patch<{ clearanceStatus: ClearanceStatus; outstanding: number }>(
        `/exits/${exitId}/clearance`,
        input,
      ),
    [exitKeys.all],
  );
}

export function useReinstate() {
  return useLifecycleMutation(
    (employeeId: string) => api.post<{ status: string }>(`/exits/reinstate/${employeeId}`),
    [exitKeys.all],
  );
}
